import {EzLayout, EzChip, EzTextStyle} from '@ezcater/recipe';
import {formatOrderNumber} from 'util/StringUtils';
import {format} from 'date-fns';
import {useTranslation} from 'react-i18next';
import {useHistory} from 'react-router-dom';
import type {
  CompletedDeliveriesAssignmentsQuery,
  MyDeliveriesAssignmentsQuery,
} from 'services/graphql/types';
import {DeliveryStatus} from 'services/graphql/types';
import dateToTimeFormatter from 'services/dateToTimeFormatter';
import {
  CalendarCard,
  CardLink,
  Month,
  DayOfMonth,
  OrderNumber,
  RightSide,
} from './DeliveryCard.styles';

type MyDeliveriesQueryNodes = NonNullable<
  NonNullable<MyDeliveriesAssignmentsQuery['me']>['driverAccount']
>['assignments']['nodes'];

type CompletedDeliveriesQueryNodes = NonNullable<
  NonNullable<CompletedDeliveriesAssignmentsQuery['me']>['driverAccount']
>['assignments']['nodes'];

type DriverAssignment = (MyDeliveriesQueryNodes | CompletedDeliveriesQueryNodes)[number];

interface DeliveryCardProps {
  assignment: DriverAssignment;
}

const success = new Set([DeliveryStatus.Completed, DeliveryStatus.Expired]);
const neutral = new Set([DeliveryStatus.Assigned]);

function pillColor(status: DeliveryStatus) {
  if (success.has(status)) {
    return 'success' as const;
  }
  if (neutral.has(status)) {
    return 'common.grey120' as const;
  }
  return 'info' as const;
}

const DeliveryCard = ({assignment}: DeliveryCardProps) => {
  const {t} = useTranslation();

  const eventTime = new Date(assignment.eventAt);
  const history = useHistory();

  const statusPillColor = pillColor(assignment.status);

  const cardClick = () => {
    history.push(`/assignment/${assignment.id}`);
  };

  return (
    <CardLink onClick={cardClick}>
      <EzLayout layout="split">
        <EzLayout>
          <EzLayout layout="stack">
            <CalendarCard>
              <Month>{format(eventTime, 'E').toUpperCase()}</Month>
              <DayOfMonth>
                {format(eventTime, 'LLL').toUpperCase()} {eventTime.getDate()}
              </DayOfMonth>

              <EzTextStyle align="center">
                {dateToTimeFormatter(eventTime, assignment.pickupAddress.timeZoneIdentifier)}
              </EzTextStyle>
            </CalendarCard>
          </EzLayout>
          <EzLayout layout="stack">
            <div>
              <OrderNumber>{formatOrderNumber(assignment.formattedOrderNumber)}</OrderNumber>
            </div>
            <div>
              <p>{assignment.dropoffAddress.street1}</p>
              {assignment.dropoffAddress.street2 && <p>{assignment.dropoffAddress.street2}</p>}
              <p>
                {assignment.dropoffAddress.city},&nbsp;
                {assignment.dropoffAddress.state}&nbsp;
                {assignment.dropoffAddress.zip}
              </p>
            </div>
            {assignment.status !== DeliveryStatus.Unassigned && (
              <div data-testid="delivery-status-text">
                <EzChip
                  label={t(`driverDeliveryStatuses.${assignment.status}`)}
                  size="inherit"
                  color={statusPillColor}
                />
              </div>
            )}
          </EzLayout>
        </EzLayout>
        <RightSide />
      </EzLayout>
    </CardLink>
  );
};

export default DeliveryCard;
